import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authApi, money, BRAND } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, CheckCircle2, TrendingUp, ShoppingBag as BagIcon, Users as UsersIcon, Package as PkgIcon, GripVertical, ChevronUp, ChevronDown, Search } from "lucide-react";
import { ProductEditor, TestimonialEditor } from "@/components/AdminEditors";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = localStorage.getItem("preshh_admin_token");
  const client = authApi(token);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [carts, setCarts] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", kind: "percent", value: 15, applies_to: "all", max_redemptions: "", expires_at: "", active: true });
  const [toc, setToc] = useState(null);
  const [tocCode, setTocCode] = useState("");
  const [tocBusy, setTocBusy] = useState(false);
  const [productEditor, setProductEditor] = useState({ open: false, item: null });
  const [testEditor, setTestEditor] = useState({ open: false, item: null });
  const [dragIdx, setDragIdx] = useState(null);

  const persistOrder = (list) => {
    setTestimonials(list);
    client.put("/admin/testimonials/reorder", { ids: list.map((t) => t.id) })
      .then(() => toast.success("Testimonial order saved"))
      .catch(() => toast.error("Could not save order"));
  };
  const moveTestimonial = (from, to) => {
    if (to < 0 || to >= testimonials.length || from === to) return;
    const list = [...testimonials];
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    persistOrder(list);
  };

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    client.get("/orders").then((r) => setOrders(r.data)).catch(() => { localStorage.removeItem("preshh_admin_token"); nav("/admin"); });
    client.get("/professional-leads").then((r) => setLeads(r.data)).catch(() => {});
    client.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
    client.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    client.get("/admin/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    client.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    client.get("/admin/abandoned-carts").then((r) => setCarts(r.data)).catch(() => {});
    client.get("/admin/knowledge").then((r) => setKnowledge(r.data)).catch(() => {});
    client.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    client.get("/admin/toconline/status").then((r) => setToc(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const setOrderStatus = async (o, s) => {
    try { await client.put(`/orders/${o.id}`, { status: s }); setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, status: s } : x)); toast.success("Order updated"); }
    catch { toast.error("Failed to update"); }
  };

  const setTracking = async (o, tracking_number) => {
    try { await client.put(`/orders/${o.id}`, { tracking_number }); setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, tracking_number } : x)); toast.success("Tracking saved"); }
    catch { toast.error("Failed"); }
  };

  const matchOrderFilter = (o, f) => {
    const cancelled = o.status === "cancelled" || o.status === "refunded";
    if (f === "all") return true;
    if (f === "cancelled") return cancelled;
    if (f === "shipped") return !cancelled && (o.status === "shipped" || o.status === "delivered");
    if (f === "paid") return !cancelled && o.payment_status === "paid";
    if (f === "pending") return !cancelled && o.payment_status !== "paid";
    return true;
  };


  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.session_id || o.id}? This permanently removes the record and cannot be undone.`)) return;
    try {
      await client.delete(`/orders/${o.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success("Order deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete order"); }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Delete customer ${c.email || c.name}? This permanently removes the account and cannot be undone.`)) return;
    try {
      await client.delete(`/admin/customers/${c.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Customer deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete customer"); }
  };

  const resendReceipt = async (o) => {
    const to = window.prompt("Send the receipt to which email?", o.email || "");
    if (to === null) return;
    try {
      const { data } = await client.post(`/admin/orders/${o.id}/resend-receipt`, to.trim() ? { to: to.trim() } : {});
      toast.success(`Receipt sent to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send receipt"); }
  };

  const previewReceipt = async (o) => {
    try {
      const { data } = await client.get(`/admin/orders/${o.id}/receipt-preview`, { responseType: "text" });
      const w = window.open("", "_blank", "noopener,noreferrer,width=680,height=820");
      if (!w) { toast.error("Pop-up blocked — allow pop-ups to preview the receipt."); return; }
      w.document.open();
      w.document.write(data);
      w.document.close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load receipt preview"); }
  };

  const buyLabel = async (o) => {
    if (!window.confirm(`Buy a shipping label for order ${o.session_id}? This uses your Shippo account.`)) return;
    try {
      const { data } = await client.post(`/admin/orders/${o.id}/buy-label`, {});
      const r = data.result || {};
      if (data.tracking_number || r.ok) {
        toast.success(`Label ready · tracking ${data.tracking_number || r.tracking_number || ""}`);
      } else {
        toast.message(`Label not created: ${r.detail || r.error || data.fulfillment_status || "unknown"}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to buy label"); }
  };

  const toggleReviewVerified = async (r) => {
    try { await client.put(`/admin/reviews/${r.id}`, { verified: !r.verified }); load(); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };

  const sendDigest = async () => {
    if (!window.confirm("Send the weekly digest email to the admin now?")) return;
    try {
      const { data } = await client.post("/admin/weekly-digest/send", {});
      toast.success(`Digest sent to ${data.to} (${data.week_label})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    }
  };

  const runAbandoned = async () => {
    try {
      const { data } = await client.post("/admin/abandoned-carts/run", {});
      toast.success(`Abandoned-cart run: ${data.sent} email${data.sent === 1 ? "" : "s"} sent`);
      load();
    } catch { toast.error("Failed to run abandoned-cart job"); }
  };

  const toggleKnowledge = async (doc) => {
    try {
      await client.put(`/admin/knowledge/${doc.id}`, { active: !doc.active });
      load();
      toast.success(`${doc.name} ${!doc.active ? "activated" : "deactivated"}`);
    } catch { toast.error("Failed to toggle"); }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim()) { toast.error("Code required"); return; }
    try {
      const payload = { ...newCoupon, value: parseFloat(newCoupon.value) || 0 };
      if (!payload.max_redemptions) delete payload.max_redemptions; else payload.max_redemptions = parseInt(payload.max_redemptions);
      if (!payload.expires_at) delete payload.expires_at;
      await client.post("/admin/coupons", payload);
      toast.success(`Coupon ${payload.code.toUpperCase()} created + synced to Stripe`);
      setNewCoupon({ code: "", kind: "percent", value: 15, applies_to: "all", max_redemptions: "", expires_at: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create coupon"); }
  };

  const toggleCoupon = async (c) => {
    try { await client.put(`/admin/coupons/${c.id}`, { active: !c.active }); load(); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };

  const deleteCoupon = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await client.delete(`/admin/coupons/${c.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const deleteReview = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try { await client.delete(`/admin/reviews/${r.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const logout = () => { localStorage.removeItem("preshh_admin_token"); nav("/admin"); };

  const refreshToc = () => client.get("/admin/toconline/status").then((r) => setToc(r.data)).catch(() => {});

  const authorizeToc = () => {
    if (toc?.authorize_url) window.open(toc.authorize_url, "_blank", "noopener,noreferrer");
  };

  const exchangeToc = async () => {
    let code = tocCode.trim();
    // Accept a full pasted redirect URL and extract the ?code= value automatically.
    if (code.includes("code=")) {
      try {
        const m = code.match(/[?&]code=([^&\s]+)/);
        if (m) code = decodeURIComponent(m[1]);
      } catch { /* keep raw */ }
    }
    if (!code) { toast.error("Paste the authorization code (or the full redirect URL) first"); return; }
    setTocBusy(true);
    try {
      await client.post("/admin/toconline/exchange", { code });
      setTocCode("");
      await refreshToc();
      toast.success("TOConline authorized — invoices can now be issued");
    } catch (e) { toast.error(e?.response?.data?.detail || "Authorization failed"); }
    finally { setTocBusy(false); }
  };

  const issueInvoice = async (o) => {
    if (!window.confirm(`Issue a real fiscal invoice (TOConline) for order ${o.session_id}? This creates an official document.`)) return;
    try {
      const { data } = await client.post(`/admin/orders/${o.id}/invoice`, {});
      if (data.status === "issued" || data.status === "already_issued") {
        toast.success(`Invoice ${data.document_no || ""} issued`);
      } else {
        toast.message(`Invoice not issued: ${data.reason || data.status}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to issue invoice"); }
  };

  const viewInvoicePdf = async (o) => {
    try {
      const { data } = await client.get(`/admin/orders/${o.id}/invoice-pdf`);
      if (data.pdf_url) window.open(data.pdf_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF not available yet"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={BRAND.logo} alt="Preshh" className="h-7" /><span className="text-white/60 text-sm">Admin</span></div>
          <Button variant="ghost" onClick={logout} className="text-white/70 hover:text-white gap-2" data-testid="admin-logout"><LogOut size={16} /> Logout</Button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="bg-[#111] border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabOrders} value="orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabProducts} value="products">Products ({products.length})</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials ({testimonials.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabLeads} value="leads">Pro Leads ({leads.length})</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabContact} value="contact">Messages ({contacts.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-customers" value="customers">Customers ({customers.length})</TabsTrigger>
            <TabsTrigger value="carts" data-testid="admin-tab-carts">Abandoned Carts ({carts.length})</TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="admin-tab-knowledge">Theo Knowledge ({knowledge.length})</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons ({coupons.length})</TabsTrigger>
            <TabsTrigger value="billing" data-testid="admin-tab-billing" className="data-[state=inactive]:bg-[#00B5D8]/15 data-[state=inactive]:text-[#00B5D8] data-[state=active]:bg-[#00B5D8] data-[state=active]:text-black font-semibold">Billing · Faturação</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-6" data-testid="admin-overview">
            {!stats ? <p className="text-white/50 py-8 text-center">Loading analytics…</p> : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={TrendingUp} label="Revenue today" value={money(stats.revenue_today)} sub={`${stats.orders_today} paid orders today`} />
                  <StatCard icon={TrendingUp} label="Revenue this month" value={money(stats.revenue_month)} sub={`AOV ${money(stats.aov)}`} />
                  <StatCard icon={BagIcon} label="Paid orders" value={stats.orders_total} sub={`${stats.orders_pending} pending · ${stats.orders_shipped} shipped`} />
                  <StatCard icon={UsersIcon} label="Customers" value={stats.total_customers} sub={`+${stats.new_customers_month} this month`} />
                </div>

                <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
                  <div className="flex justify-between items-baseline mb-4">
                    <h3 className="font-display text-xl">Revenue · last 14 days</h3>
                    <p className="text-xs text-white/50">Total lifetime: {money(stats.revenue_all_time)}</p>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats.revenue_series}>
                        <CartesianGrid stroke="#1f1f22" strokeDasharray="4 4" />
                        <XAxis dataKey="date" stroke="#71717a" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                        <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `€${v}`} />
                        <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => money(v)} />
                        <Line type="monotone" dataKey="revenue" stroke="#00B5D8" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#00B5D8" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="admin-top-products">
                    <h3 className="font-display text-xl mb-4 flex items-center gap-2"><PkgIcon size={18} className="text-[#00B5D8]" /> Top products</h3>
                    {stats.top_products.length === 0 ? <p className="text-white/50 text-sm">No sales yet.</p> : (
                      <ul className="space-y-3">
                        {stats.top_products.map((p) => (
                          <li key={p.slug} className="flex justify-between text-sm">
                            <span className="text-white/80 truncate max-w-[65%]">{p.name}</span>
                            <span className="text-white/50">{p.units} units · <span className="text-[#00B5D8]">{money(p.revenue)}</span></span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
                    <h3 className="font-display text-xl mb-4">Where our customers are</h3>
                    {stats.countries.length === 0 ? <p className="text-white/50 text-sm">No shipping data yet.</p> : (
                      <ul className="space-y-2">
                        {stats.countries.map((c) => (
                          <li key={c.country} className="flex justify-between text-sm items-center">
                            <span className="text-white/80">{c.country}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full bg-[#00B5D8]" style={{ width: `${Math.min(100, (c.orders / stats.countries[0].orders) * 100)}%` }} />
                              </div>
                              <span className="text-white/50 tabular-nums w-8 text-right">{c.orders}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <p className="text-xs text-white/40 text-right">Snapshot generated {new Date(stats.generated_at).toLocaleTimeString()}</p>
                <div className="pt-2 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">Weekly digest email</p>
                    <p className="text-xs text-white/50">Auto-sent every Monday 09:00 UTC. Trigger manually to preview.</p>
                  </div>
                  <Button data-testid="admin-send-digest" onClick={sendDigest} className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full">
                    Send digest now
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6 space-y-3">
            {(() => {
              const counts = {
                all: orders.length,
                pending: orders.filter((o) => matchOrderFilter(o, "pending")).length,
                paid: orders.filter((o) => matchOrderFilter(o, "paid")).length,
                shipped: orders.filter((o) => matchOrderFilter(o, "shipped")).length,
                cancelled: orders.filter((o) => matchOrderFilter(o, "cancelled")).length,
              };
              const filters = [
                ["all", "All"], ["pending", "Pending"], ["paid", "Paid"], ["shipped", "Shipped"], ["cancelled", "Cancelled"],
              ];
              return (
                <div className="flex flex-wrap gap-2" data-testid="admin-order-filters">
                  {filters.map(([key, label]) => (
                    <button
                      key={key}
                      data-testid={`order-filter-${key}`}
                      onClick={() => setOrderFilter(key)}
                      className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                        orderFilter === key
                          ? "border-[#00B5D8] bg-[#00B5D8] text-black font-semibold"
                          : "border-white/15 text-white/70 hover:border-white/30 hover:text-white"
                      }`}>
                      {label} ({counts[key]})
                    </button>
                  ))}
                </div>
              );
            })()}
            <Input
              data-testid="admin-order-search"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by order code (PR-XXXXX), email or reference…"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 max-w-md"
            />
            {(() => {
              const q = orderSearch.trim().toLowerCase();
              const filtered = orders
                .filter((o) => matchOrderFilter(o, orderFilter))
                .filter((o) => !q || [o.order_no, o.email, o.session_id, o.customer_name]
                  .some((v) => (v || "").toLowerCase().includes(q)));
              if (orders.length === 0) return <p className="text-white/50 py-8 text-center">No orders yet.</p>;
              if (filtered.length === 0) return <p className="text-white/50 py-8 text-center" data-testid="admin-orders-empty-filter">No orders match this filter.</p>;
              return filtered.map((o) => (
              <div key={o.id} className="relative bg-[#111] border border-white/10 rounded-2xl p-5">
                <button
                  onClick={() => deleteOrder(o)}
                  data-testid={`order-delete-${o.id}`}
                  title="Delete order"
                  aria-label="Delete order"
                  className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-red-500/40 text-red-400 hover:text-white hover:bg-red-500 hover:border-red-500 transition-colors">
                  <Trash2 size={15} />
                </button>
                <div className="flex flex-wrap justify-between gap-4 pr-10">
                  <div>
                    <p className="text-xs text-white/50">{new Date(o.created_at).toLocaleString()} · {o.payment_provider || "stripe"}</p>
                    {o.order_no && <p className="font-mono text-sm font-bold text-[#00B5D8]" data-testid={`order-no-${o.id}`}>{o.order_no}</p>}
                    <p className="font-mono text-xs text-white/60">{o.session_id}</p>
                    <p className="mt-1 text-sm">{o.email || "guest"} · {o.shipping_country}</p>
                    {(o.phone || o.id_document) && (
                      <p className="mt-1 text-xs text-white/50" data-testid={`order-contact-${o.id}`}>
                        {o.phone ? `☎ ${o.phone}` : ""}{o.phone && o.id_document ? " · " : ""}{o.id_document ? `ID: ${o.id_document}` : ""}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-white/70">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl">{money(o.amount, o.currency)}</p>
                    <p className="text-xs uppercase text-[#00B5D8] mt-1">{o.status} / {o.payment_status}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-3 flex-wrap items-center">
                  <Select value={o.status} onValueChange={(v) => setOrderStatus(o, v)}>
                    <SelectTrigger className="w-56 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] text-white border-white/10">
                      {["pending_payment", "paid", "processing", "ready_to_ship", "shipped", "delivered", "cancelled", "refunded"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Tracking number" defaultValue={o.tracking_number || ""}
                    onBlur={(e) => e.target.value !== (o.tracking_number || "") && setTracking(o, e.target.value)}
                    className="max-w-xs bg-white/5 border-white/10" />
                </div>
                {o.payment_status === "paid" && (
                  <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-3 items-center text-xs">
                    <span className="text-white/50">Shipping:</span>                    <span className={`px-2 py-1 rounded-full border ${
                      o.fulfillment_status === "label_purchased" ? "border-green-400/50 text-green-300"
                      : o.fulfillment_status === "label_failed" ? "border-red-400/50 text-red-300"
                      : "border-white/20 text-white/60"}`} data-testid={`order-fulfil-${o.id}`}>
                      {o.fulfillment_status === "label_purchased" ? `Label bought${o.shipping_carrier ? ` · ${o.shipping_carrier}` : ""}`
                        : o.fulfillment_status === "label_failed" ? "Label failed"
                        : o.fulfillment_status === "awaiting_address" ? "Awaiting address"
                        : "Not shipped"}
                    </span>
                    {o.tracking_url && (
                      <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#00B5D8] hover:underline">Track</a>
                    )}
                    {o.shippo_label_url && (
                      <a href={o.shippo_label_url} target="_blank" rel="noreferrer" data-testid={`order-label-${o.id}`} className="text-[#00B5D8] hover:underline">Print label (PDF)</a>
                    )}
                    {o.fulfillment_error && <span className="text-red-300/80">{o.fulfillment_error}</span>}
                    {!o.shippo_transaction_id && (
                      <Button onClick={() => buyLabel(o)} data-testid={`order-buy-label-${o.id}`}
                        className="h-7 px-3 rounded-full bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold text-xs">
                        {o.fulfillment_status === "label_failed" ? "Retry label" : "Buy label"}
                      </Button>
                    )}
                    <Button onClick={() => previewReceipt(o)} data-testid={`order-preview-receipt-${o.id}`}
                      className="h-7 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs">
                      Preview receipt
                    </Button>
                    <Button onClick={() => resendReceipt(o)} data-testid={`order-resend-receipt-${o.id}`}
                      className="h-7 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs">
                      Resend receipt
                    </Button>
                  </div>
                )}
              </div>
            ));
            })()}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="admin-product-new" onClick={() => setProductEditor({ open: true, item: null })} className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold gap-2"><Plus size={16} /> New product</Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {products.map((p) => (
                <div key={p.id} className="bg-[#111] border border-white/10 rounded-2xl p-4 flex gap-4">
                  <img src={p.images?.[0]} alt="" className="w-24 h-24 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{p.name}</p>
                    <p className="text-xs text-white/50">{p.slug} · {p.category}</p>
                    <p className="text-[#00B5D8] mt-1">{money(p.price, p.currency)}</p>
                    <p className="text-xs mt-1 text-white/50">Stock: {p.stock} · {p.active ? "Active" : "Inactive"}{p.featured ? " · Featured" : ""}</p>
                  </div>
                  <button onClick={() => setProductEditor({ open: true, item: p })} data-testid={`admin-product-edit-${p.slug}`} className="text-white/60 hover:text-white p-2 self-start"><Pencil size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-6">
            <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
              <p className="text-xs text-white/50">Drag the handle, or use the arrows, to reorder how testimonials appear on the homepage.</p>
              <Button onClick={() => setTestEditor({ open: true, item: null })} data-testid="admin-testimonial-new" className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold gap-2"><Plus size={16} /> New testimonial</Button>
            </div>
            <div className="space-y-3">
              {testimonials.map((t, idx) => (
                <div key={t.id} data-testid={`admin-testimonial-row-${idx}`}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveTestimonial(dragIdx, idx); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`bg-[#111] border rounded-2xl p-4 flex gap-3 items-start transition-colors ${dragIdx === idx ? "border-[#00B5D8] opacity-60" : "border-white/10"}`}>
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <span className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white" title="Drag to reorder" aria-hidden="true"><GripVertical size={18} /></span>
                    <button onClick={() => moveTestimonial(idx, idx - 1)} disabled={idx === 0} data-testid={`admin-testimonial-up-${idx}`} aria-label="Move up" className="text-white/50 hover:text-[#00B5D8] disabled:opacity-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] rounded"><ChevronUp size={16} /></button>
                    <button onClick={() => moveTestimonial(idx, idx + 1)} disabled={idx === testimonials.length - 1} data-testid={`admin-testimonial-down-${idx}`} aria-label="Move down" className="text-white/50 hover:text-[#00B5D8] disabled:opacity-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] rounded"><ChevronDown size={16} /></button>
                  </div>
                  {t.image ? <img src={t.image} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-white/5" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{t.name} <span className="text-xs text-white/40">· order {t.order}</span></p>
                    <p className="text-xs text-white/50 mb-1">{t.role || "—"}</p>
                    <p className="text-sm text-white/70 line-clamp-2">{t.quote || <em className="text-white/40">no quote yet</em>}</p>
                    <p className="text-xs mt-1 text-white/50">{t.published ? "Published" : "Draft"} · {t.language || "—"}</p>
                  </div>
                  <button onClick={() => setTestEditor({ open: true, item: t })} aria-label={`Edit ${t.name}`} className="text-white/60 hover:text-white p-2"><Pencil size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-6 space-y-3">
            {reviews.length === 0 && <p className="text-white/50 py-8 text-center">No reviews yet.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="bg-[#111] border border-white/10 rounded-2xl p-4">
                <div className="flex justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{r.name} <span className="text-xs text-white/40">· {r.product_slug} · {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span></p>
                    <p className="text-xs text-white/50 mb-1">{r.country || "—"} · {new Date(r.created_at).toLocaleDateString()}</p>
                    <p className="text-sm text-white/80">{r.text}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-xs">
                    <button onClick={() => toggleReviewVerified(r)} className={`px-3 py-1 rounded-full border ${r.verified ? "border-[#00B5D8] text-[#00B5D8]" : "border-white/20 text-white/60"} flex items-center gap-1`}>
                      <CheckCircle2 size={12} /> {r.verified ? "Verified" : "Verify"}
                    </button>
                    <button onClick={() => deleteReview(r)} className="text-red-400 hover:text-red-300 flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6 space-y-3">
            {leads.length === 0 && <p className="text-white/50 py-8 text-center">No leads yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="bg-[#111] border border-white/10 rounded-2xl p-5">
                <div className="flex justify-between">
                  <div><p className="font-bold">{l.name} · {l.venue}</p><p className="text-sm text-white/60">{l.email} · {l.phone}</p><p className="text-xs text-white/50">{l.country} · {l.business_type}</p></div>
                  <p className="text-xs text-white/50">{new Date(l.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-3 text-sm text-white/70">{l.goals}</p>
              </div>
            ))}
          </TabsContent>

          {/* CONTACT */}
          <TabsContent value="contact" className="mt-6 space-y-3">
            {contacts.length === 0 && <p className="text-white/50 py-8 text-center">No messages yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="bg-[#111] border border-white/10 rounded-2xl p-5">
                <div className="flex justify-between"><p className="font-bold">{c.name} · {c.email}</p><p className="text-xs text-white/50">{new Date(c.created_at).toLocaleString()}</p></div>
                <p className="mt-1 text-sm text-white/60">{c.subject}</p>
                <p className="mt-3 text-sm text-white/80 whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6 space-y-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                data-testid="admin-customer-search"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customers by name or email…"
                className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
            </div>
            {customers.length === 0 && <p className="text-white/50 py-8 text-center">No customers yet.</p>}
            {(() => {
              const q = customerSearch.trim().toLowerCase();
              const list = q
                ? customers.filter((c) => `${c.name || ""} ${c.email || ""}`.toLowerCase().includes(q))
                : customers;
              if (customers.length > 0 && list.length === 0)
                return <p className="text-white/50 py-8 text-center" data-testid="admin-customers-empty-search">No customers match “{customerSearch}”.</p>;
              return list.map((c) => (
              <div key={c.id} className="relative bg-[#111] border border-white/10 rounded-2xl p-5">
                <button
                  onClick={() => deleteCustomer(c)}
                  data-testid={`customer-delete-${c.id}`}
                  title="Delete customer"
                  aria-label="Delete customer"
                  className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-red-500/40 text-red-400 hover:text-white hover:bg-red-500 hover:border-red-500 transition-colors">
                  <Trash2 size={15} />
                </button>
                <div className="flex flex-wrap justify-between gap-3 pr-10">
                  <div>
                    <p className="font-bold">{c.name}</p>
                    <p className="text-sm text-white/60">{c.email} · {c.phone || "no phone"}</p>
                    <p className="text-xs text-white/50 mt-1">Birthday: {c.birthday || "—"} · Joined: {new Date(c.created_at).toLocaleDateString()}</p>
                    {c.referral_code && <p className="text-xs mt-1 text-white/60">Referral code: <span className="text-[#00B5D8] font-mono">{c.referral_code}</span> · Credit: €{(c.referral_credit_eur || 0).toFixed(2)}</p>}
                  </div>
                  <div className="text-right text-xs space-y-1">
                    {c.is_birthday_month && <p className="text-[#00B5D8]">🎉 Birthday month</p>}
                    <p className={c.birthday_benefit_used_this_year ? "text-white/60" : "text-white/80"}>
                      {c.birthday_benefit_used_this_year ? "✓ Benefit used this year" : "Benefit not yet redeemed"}
                    </p>
                  </div>
                </div>
              </div>
              ));
            })()}
          </TabsContent>

          {/* ABANDONED CARTS */}
          <TabsContent value="carts" className="mt-6 space-y-3" data-testid="admin-carts-tab">
            <div className="flex justify-between items-center flex-wrap gap-3 mb-2">
              <p className="text-sm text-white/60">Emails fire automatically 24h after a cart is left idle. Trigger the job manually to preview.</p>
              <Button onClick={runAbandoned} data-testid="admin-run-abandoned" className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full">Run abandoned-cart job now</Button>
            </div>
            {carts.length === 0 && <p className="text-white/50 py-8 text-center">No tracked carts yet.</p>}
            {carts.map((c, i) => (
              <div key={c.id || i} className="bg-[#111] border border-white/10 rounded-2xl p-5">
                <div className="flex flex-wrap justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs text-white/50">Updated {new Date(c.updated_at).toLocaleString()}</p>
                    <p className="mt-1 text-sm">{c.email || <span className="text-white/40">no email captured</span>}</p>
                    <p className="mt-1 text-sm text-white/70 truncate">{(c.items || []).map((it) => `${it.name} × ${it.quantity}`).join(", ")}</p>
                  </div>
                  <div className="text-right text-xs space-y-1">
                    <p className="font-display text-lg text-white">{money(c.subtotal || 0)}</p>
                    {c.converted ? <p className="text-[#00B5D8]">✓ Converted</p>
                      : c.notified_at ? <p className="text-white/60">✉ Notified {new Date(c.notified_at).toLocaleDateString()}</p>
                      : <p className="text-white/50">Pending 24h</p>}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* KNOWLEDGE */}
          <TabsContent value="knowledge" className="mt-6 space-y-3" data-testid="admin-knowledge-tab">
            <p className="text-sm text-white/60 mb-2">Sources Theo uses to answer. LEVEL 1 = official product/manual (highest authority). LEVEL 2 = approved educational material. Deactivate a doc to remove it from Theo's context on the next chat.</p>
            {knowledge.map((d) => (
              <div key={d.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{d.name}</p>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${d.source_type === "LEVEL_1" ? "bg-[#00B5D8]/20 text-[#00B5D8]" : "bg-white/10 text-white/70"}`}>{d.source_type.replace("_", " ")}</span>
                  </div>
                  <p className="text-xs text-white/50 mt-1">{d.kind} · {d.product} · v{d.version} · {d.language.toUpperCase()} · added {new Date(d.added_at).toLocaleDateString()}</p>
                </div>
                <button data-testid={`admin-knowledge-toggle-${d.id}`} onClick={() => toggleKnowledge(d)}
                  className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${d.active ? "border-[#00B5D8] text-[#00B5D8] hover:bg-[#00B5D8]/10" : "border-white/20 text-white/50 hover:border-white/40"}`}>
                  {d.active ? "Active" : "Inactive"}
                </button>
              </div>
            ))}
          </TabsContent>

          {/* COUPONS */}
          <TabsContent value="coupons" className="mt-6 space-y-4" data-testid="admin-coupons-tab">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-5">
              <p className="text-sm font-semibold mb-1">Create a new coupon</p>
              <p className="text-xs text-white/50 mb-4">Coupons are created directly in Stripe as native promotion codes. Discounts are real — customers redeem them at checkout.</p>
              <div className="grid md:grid-cols-6 gap-3">
                <Input data-testid="coupon-code" placeholder="CODE (e.g. LAUNCH15)" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })} className="bg-white/5 border-white/10 md:col-span-2" />
                <Select value={newCoupon.kind} onValueChange={(v) => setNewCoupon({ ...newCoupon, kind: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] text-white border-white/10">
                    <SelectItem value="percent">% off</SelectItem>
                    <SelectItem value="amount">€ off</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="coupon-value" type="number" step="0.01" placeholder="Value" value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })} className="bg-white/5 border-white/10" />
                <Input placeholder="Max uses (opt.)" value={newCoupon.max_redemptions} onChange={(e) => setNewCoupon({ ...newCoupon, max_redemptions: e.target.value })} className="bg-white/5 border-white/10" />
                <Input type="date" value={newCoupon.expires_at?.slice(0, 10) || ""} onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : "" })} className="bg-white/5 border-white/10" />
              </div>
              <div className="mt-3 flex justify-end">
                <Button data-testid="coupon-create" onClick={createCoupon} className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full">Create coupon</Button>
              </div>
            </div>
            {coupons.length === 0 && <p className="text-white/50 py-8 text-center">No coupons yet.</p>}
            {coupons.map((c) => {
              const expired = c.expires_at && new Date(c.expires_at) < new Date();
              const exhausted = c.max_redemptions && (c.redemptions || 0) >= c.max_redemptions;
              const status = !c.active ? "Disabled" : expired ? "Expired" : exhausted ? "Exhausted" : "Active";
              const statusClass = status === "Active" ? "text-[#00B5D8]" : status === "Disabled" ? "text-white/40" : "text-red-300";
              return (
                <div key={c.id} className="bg-[#111] border border-white/10 rounded-2xl p-5">
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="min-w-0">
                      <p className="font-mono text-lg tracking-widest">{c.code}</p>
                      <p className="text-xs text-white/50 mt-1">
                        {c.kind === "percent" ? `${c.value}% off` : `€${c.value} off`} · {c.applies_to}
                        {c.max_redemptions ? ` · ${c.redemptions || 0}/${c.max_redemptions} redemptions` : ""}
                        {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                      </p>
                      <p className={`text-xs uppercase tracking-widest mt-2 ${statusClass}`}>{status}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => toggleCoupon(c)} className="text-xs px-3 py-1 rounded-full border border-white/20 hover:border-white/40">{c.active ? "Deactivate" : "Activate"}</button>
                      <button onClick={() => deleteCoupon(c)} className="text-xs px-3 py-1 rounded-full border border-red-400/30 text-red-300 hover:bg-red-400/10">Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* BILLING (TOConline) */}
          <TabsContent value="billing" className="mt-6 space-y-5" data-testid="admin-billing-tab">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-display text-xl">TOConline — automatic invoicing</h3>
                  <p className="text-xs text-white/50 mt-1">Portuguese fiscal invoices (Fatura) are issued automatically after a paid PT order. First-time setup needs a one-time OAuth consent.</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-3 py-1 rounded-full border ${toc?.configured ? "border-[#00B5D8] text-[#00B5D8]" : "border-white/20 text-white/50"}`}>
                    {toc?.configured ? "Configured" : "Not configured"}
                  </span>
                  <span className={`px-3 py-1 rounded-full border ${toc?.authorized ? "border-green-400 text-green-400" : "border-amber-400/60 text-amber-300"}`} data-testid="toc-auth-badge">
                    {toc?.authorized ? "Authorized" : "Not authorized"}
                  </span>
                </div>
              </div>

              {toc && !toc.authorized && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl bg-[#0a0a0a] border border-white/10 p-4 text-sm text-white/70 space-y-2">
                    <p className="font-semibold text-white">One-time authorization — 3 steps</p>
                    <p><span className="text-[#00B5D8] font-semibold">1.</span> Click <em>Open TOConline consent</em> and log in with your TOConline account, then approve access.</p>
                    <p><span className="text-[#00B5D8] font-semibold">2.</span> You'll land on a page ("Your call is authenticated"). Copy the <strong>full URL</strong> from your browser's address bar — it contains <code className="text-[#00B5D8]">?code=…</code>.</p>
                    <p><span className="text-[#00B5D8] font-semibold">3.</span> Paste that URL (or just the code) below and click <em>Complete authorization</em>. Nothing is sent through chat — it stays in your browser.</p>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Button data-testid="toc-authorize" onClick={authorizeToc} disabled={!toc.configured || !toc.authorize_url}
                      className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full">Open TOConline consent</Button>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Input data-testid="toc-code" placeholder="Paste the full redirect URL (or just the code) here" value={tocCode}
                      onChange={(e) => setTocCode(e.target.value)} className="bg-white/5 border-white/10 flex-1 min-w-[240px] font-mono" />
                    <Button data-testid="toc-exchange" onClick={exchangeToc} disabled={tocBusy}
                      className="bg-white text-black hover:bg-white/90 font-bold rounded-full">{tocBusy ? "Authorizing…" : "Complete authorization"}</Button>
                  </div>
                </div>
              )}

              {toc?.authorized && (
                <div className="mt-6 rounded-xl bg-green-400/5 border border-green-400/20 p-4 text-sm text-green-200">
                  ✓ TOConline is authorized. Paid Portuguese orders are invoiced automatically. You can also issue invoices manually below.
                </div>
              )}
            </div>

            <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
              <h3 className="font-display text-lg mb-1">Paid orders &amp; invoices</h3>
              <p className="text-xs text-white/50 mb-4">Issue or re-open the fiscal document for a paid order. Non-PT (0% VAT) orders stay pending for manual handling.</p>
              {orders.filter((o) => o.payment_status === "paid").length === 0 && (
                <p className="text-white/50 py-6 text-center">No paid orders yet.</p>
              )}
              <div className="space-y-3">
                {orders.filter((o) => o.payment_status === "paid").map((o) => (
                  <div key={o.id} className="rounded-xl bg-[#0a0a0a] border border-white/10 p-4 flex flex-wrap justify-between items-center gap-3" data-testid={`toc-order-${o.id}`}>
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-white/60">{o.session_id}</p>
                      <p className="text-sm mt-1">{o.email || "guest"} · {(o.billing_country || o.shipping_country || "—")} {o.nif ? `· NIF ${o.nif}` : ""}</p>
                      <p className="text-xs text-white/50 mt-1">{money(o.amount, o.currency)} · <span className="uppercase text-[#00B5D8]">{o.invoice_status || "not invoiced"}</span>{o.invoice_number ? ` · #${o.invoice_number}` : ""}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {o.invoice_status === "invoiced" ? (
                        <Button onClick={() => viewInvoicePdf(o)} data-testid={`toc-pdf-${o.id}`}
                          className="rounded-full bg-white/10 hover:bg-white/20 text-white text-sm">View PDF</Button>
                      ) : (
                        <Button onClick={() => issueInvoice(o)} disabled={!toc?.authorized} data-testid={`toc-issue-${o.id}`}
                          className="rounded-full bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold text-sm">Issue invoice</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {productEditor.open && (
        <ProductEditor open={productEditor.open} product={productEditor.item} client={client}
          onClose={() => setProductEditor({ open: false, item: null })} onSaved={load} />
      )}
      {testEditor.open && (
        <TestimonialEditor open={testEditor.open} testimonial={testEditor.item} client={client}
          onClose={() => setTestEditor({ open: false, item: null })} onSaved={load} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 text-white/60">
        <Icon size={16} className="text-[#00B5D8]" />
        <p className="text-xs uppercase tracking-widest">{label}</p>
      </div>
      <p className="font-display text-3xl mt-2">{value}</p>
      {sub && <p className="text-xs text-white/50 mt-1">{sub}</p>}
    </div>
  );
}
